import type { GeocodeResult } from "@/lib/contracts/geocode";
import type { MapSpec } from "@/lib/map";

/**
 * `map_rendered_first` north-star helpers (M12 §6). The registry requires the
 * FULL shape `{time_to_first_map_ms, marker_count, style, geocode_strategy}` —
 * these pure functions derive `style` + `geocode_strategy` from the real render
 * inputs so the editor emits the north-star with true data (never the M0
 * placeholder), and are unit-tested against `validateEvent`.
 */

/** The render style descriptor: how the points are drawn on the plate. */
export function deriveMapStyle(spec: MapSpec): string {
  if (spec.heat.enabled) return "heatmap";
  if (spec.clustering.enabled) return "clusters";
  return "pins";
}

/**
 * How the coordinates on this map were produced:
 *   - `coordinates`  — lat/lng came straight off the rows (no geocoding),
 *   - `user_scoped`  — at least one row resolved via a user-scoped provider (Google),
 *   - `shared_cache` — resolved via the shared-cacheable OSS geocode path.
 */
export function deriveGeocodeStrategy(mode: "coordinate" | "address", results: readonly GeocodeResult[]): string {
  if (mode === "coordinate") return "coordinates";
  if (results.some((r) => r.policy === "user_scoped")) return "user_scoped";
  return "shared_cache";
}

export interface MapRenderedFirstProps {
  time_to_first_map_ms: number;
  marker_count: number;
  style: string;
  geocode_strategy: string;
  map_id: string;
}

/** Assemble the full §6 `map_rendered_first` payload from the real render inputs. */
export function buildMapRenderedFirstProps(input: {
  mapId: string;
  markerCount: number;
  spec: MapSpec;
  mode: "coordinate" | "address";
  results: readonly GeocodeResult[];
  elapsedMs: number;
}): MapRenderedFirstProps {
  return {
    time_to_first_map_ms: Math.max(0, Math.round(input.elapsedMs)),
    marker_count: input.markerCount,
    style: deriveMapStyle(input.spec),
    geocode_strategy: deriveGeocodeStrategy(input.mode, input.results),
    map_id: input.mapId,
  };
}

const FIRST_MAP_STORAGE_PREFIX = "plotzio:map_rendered_first:";

/**
 * Once-per-IDENTITY guard for the north-star: returns true only the first time
 * an identity records a first map, persisting the fact so a reload for the same
 * anon/user identity doesn't refire. Falls back to "fire" when storage is
 * unavailable (the caller's per-session ref still prevents an in-session refire).
 */
export function claimFirstMapForIdentity(identity: string, storage?: Pick<Storage, "getItem" | "setItem">): boolean {
  try {
    const store = storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
    if (!store) return true;
    const key = `${FIRST_MAP_STORAGE_PREFIX}${identity}`;
    if (store.getItem(key)) return false;
    store.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}
